/**
 * Flag a confirmed purchase as recurring (Task 21).
 * Writes recurring_charges, links the transaction, outboxes both.
 */

import type { CardZappDB } from '../db/database';
import { enqueueOutbox } from '../db/outbox';
import { payloadHash } from '../db/canonicalize';
import { addMonthsClamped, businessDate, type BusinessDate } from '../domain/time';
import type { Cents } from '../domain/money';
import { asCents } from '../domain/money';
import type { SpendCategory } from '../domain/recommend/types';
import type { RecurringChargeLocal, TransactionLocal } from '../schemas/entities';

export type FlagRecurringChargeInput = {
  db: CardZappDB;
  transaction_id: string;
  /** Defaults to monthly when the user only flags (Task 21 AC). */
  frequency?: string;
  now_iso: string;
};

export type FlagRecurringChargeResult = {
  recurring_charge_id: string;
};

function stripLocal(row: object): Record<string, unknown> {
  const { _localDirty: _d, _baseServerUpdatedAt: _b, ...rest } = row as Record<string, unknown>;
  void _d;
  void _b;
  return rest;
}

async function loadTransaction(db: CardZappDB, id: string): Promise<TransactionLocal> {
  const row = await db.transactions.where('id').equals(id).first();
  if (row === undefined || row.deleted_at !== null) {
    throw new Error('transaction_not_found');
  }
  return row;
}

/**
 * Create an active recurring_charges row from a confirmed purchase and link it.
 */
export async function flagRecurringCharge(
  input: FlagRecurringChargeInput,
): Promise<FlagRecurringChargeResult> {
  const txn = await loadTransaction(input.db, input.transaction_id);
  if (txn.recurring_charge_id !== null) {
    throw new Error('already_recurring');
  }

  const now = input.now_iso;
  const recurringId = crypto.randomUUID();
  const frequency = input.frequency ?? 'monthly';
  const txnDate: BusinessDate = businessDate(String(txn.transaction_date));
  const nextDate: BusinessDate = addMonthsClamped(txnDate, 1);
  const amount: Cents = asCents(BigInt(txn.amount_cents));
  const category: SpendCategory | null = txn.category;
  const merchantId: string | null = txn.merchant_id;
  const merchantName: string = String(txn.merchant_name_raw);
  const cardId: string = String(txn.card_id);
  const txnId: string = String(txn.id);
  const baseTxn = txn._baseServerUpdatedAt;

  const recurring: RecurringChargeLocal = {
    id: recurringId,
    user_id: null,
    merchant_id: merchantId,
    merchant_name: merchantName,
    expected_amount_cents: amount,
    frequency,
    assigned_card_id: cardId,
    next_expected_date: nextDate,
    category,
    active: true,
    last_transaction_id: txnId,
    reward_reason: null,
    review_date: null,
    variance_tolerance_bps: 1000,
    created_at: now,
    updated_at: now,
    deleted_at: null,
    _localDirty: true,
    _baseServerUpdatedAt: null,
  };

  const updatedTxn: TransactionLocal = {
    ...txn,
    recurring_charge_id: recurringId,
    updated_at: now,
    _localDirty: true,
  };

  const recurringPayload = stripLocal(recurring);
  const txnPayload = stripLocal(updatedTxn);
  const recurringHash = await payloadHash(recurringPayload);
  const txnHash = await payloadHash(txnPayload);

  await input.db.transaction(
    'rw',
    [input.db.recurring_charges, input.db.transactions, input.db.outbox],
    async () => {
      await input.db.recurring_charges.put(recurring);
      await enqueueOutbox(input.db, {
        table: 'recurring_charges',
        op: 'insert',
        rowId: recurringId,
        payload: recurringPayload,
        payloadHash: recurringHash,
        baseServerUpdatedAt: null,
        createdAt: now,
      });

      await input.db.transactions.put(updatedTxn);
      await enqueueOutbox(input.db, {
        table: 'transactions',
        op: 'update',
        rowId: txnId,
        payload: txnPayload,
        payloadHash: txnHash,
        baseServerUpdatedAt: baseTxn,
        createdAt: now,
      });
    },
  );

  return { recurring_charge_id: recurringId };
}
